import { Search } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { LedgerAuditTrail } from './audit-trail-panel'
import { LEDGER_AUDIT_SUBJECTS } from './constants'
import { useLedgerPermissions } from './permissions'

interface Lookup {
  entityType: string
  entityId: string
}

/**
 * Reading the audit trail for one ledger entity.
 *
 * With the journal endpoints in place, this is no longer a substitute for a journal enquiry — the
 * journals screen reads entries and their lines directly. What the trail still carries and no other
 * read does is the *human* half: which named user acted, whether anybody countersigned, and whether
 * an attempt was refused. A journal row carries `postedByUserId` and nothing else about a person.
 *
 * Its limits are stated rather than discovered. It cannot be browsed — an entity type and an
 * identifier have to be known in advance. It is gated on `ops.audit.view` rather than on a ledger
 * permission, so a `ledger.journal.view` holder may be unable to use it at all. And the handler
 * scopes rows to the caller's accessible branches unless they hold `admin.access.all_branches`, so
 * an empty answer can mean "not yours" rather than "nothing happened".
 *
 * Reading is fine on a phone — a supervisor checking who opened a day is exactly what a phone is
 * for — so nothing here refuses a narrow screen.
 */
export function LedgerAuditLookupSection() {
  const permissions = useLedgerPermissions()

  const [entityType, setEntityType] = useState(LEDGER_AUDIT_SUBJECTS[0].entityType)
  const [identifier, setIdentifier] = useState('')
  const [lookup, setLookup] = useState<Lookup | null>(null)

  const subject =
    LEDGER_AUDIT_SUBJECTS.find((candidate) => candidate.entityType === entityType) ??
    LEDGER_AUDIT_SUBJECTS[0]

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <Search className="size-4 text-brand-text" aria-hidden />
              Who did it, and did anybody countersign
            </CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              The identifier has to be known in advance — the trail cannot be listed or searched.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="audit-subject">Record</Label>
              <Select
                value={entityType}
                onValueChange={(next) => {
                  setEntityType(next)
                  setIdentifier('')
                  setLookup(null)
                }}
              >
                <SelectTrigger id="audit-subject">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEDGER_AUDIT_SUBJECTS.map((candidate) => (
                    <SelectItem key={candidate.entityType} value={candidate.entityType}>
                      {candidate.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="audit-identifier">Identifier</Label>
              <Input
                id="audit-identifier"
                value={identifier}
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
                onChange={(event) => setIdentifier(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && identifier.trim() !== '') {
                    setLookup({ entityType, entityId: identifier.trim() })
                  }
                }}
                aria-describedby="audit-identifier-help"
              />
              <p id="audit-identifier-help" className="text-xs text-muted">
                {subject.identifierHint} {subject.provenance}
              </p>
            </div>
          </div>

          <Button
            variant="primary"
            disabled={identifier.trim() === ''}
            onClick={() => setLookup({ entityType, entityId: identifier.trim() })}
          >
            <Search />
            Read the trail
          </Button>
        </CardContent>
      </Card>

      <LedgerAuditTrail
        entityType={lookup?.entityType ?? entityType}
        entityId={lookup?.entityId ?? ''}
        canRead={permissions.canReadAudit}
        emptyHint={
          lookup === null
            ? 'Enter an identifier above. Nothing is read until you ask for it — reading the audit trail is itself an audited action.'
            : 'Check the identifier: the trail is keyed by exactly the value the writer supplied, and a near miss returns an empty page rather than an error.'
        }
      />
    </div>
  )
}
